import React from 'react';
import { AgendaData } from '../types';
import { CalendarX, Clock, Pencil, Trash2 } from 'lucide-react';

interface AgendaListProps {
  agenda: AgendaData[];
  onEditAgenda: (agenda: AgendaData) => void;
  onDeleteAgenda: (agendaId: string) => void;
  onToggleAgenda: (agendaId: string, isActive: boolean) => void;
}

const AgendaList: React.FC<AgendaListProps> = ({
  agenda,
  onEditAgenda,
  onDeleteAgenda,
  onToggleAgenda
}) => {
  if (agenda.length === 0) {
    return (
      <div style={{ padding: "32px 16px", textAlign: "center", borderRadius: 14, background: 'var(--v3-bg-subtle, rgba(255,255,255,0.02))', border: '1px dashed var(--v3-border-medium, rgba(255,255,255,0.08))' }}>
        <CalendarX size={28} color="var(--v3-text-faint, rgba(255,255,255,0.2))" style={{ margin: "0 auto 8px auto", display: "block" }} />
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--v3-text-main, #fff)' }}>No agenda items added yet</div>
        <p style={{ fontSize: 11, color: 'var(--v3-text-muted, rgba(255,255,255,0.4))', marginTop: 4, marginBottom: 0 }}>Click "Add Agenda Item" to get started.</p>
      </div>
    );
  }

  const formatTime = (time: string | undefined | null) => {
    if (!time) return '';
    const str = String(time).trim();
    if (!str) return '';

    if (/^\d{1,2}:\d{2}(:\d{2})?\s*(AM|PM)$/i.test(str)) {
      return str;
    }

    if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(str)) {
      const parts = str.split(':');
      let hours = parseInt(parts[0], 10);
      const minutes = parts[1];
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12;
      return `${hours}:${minutes} ${ampm}`;
    }

    const parsed = new Date(str.includes('T') ? str : `2000-01-01T${str}`);
    if (!isNaN(parsed.getTime())) {
      return parsed.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }

    return str;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {agenda.map((item, index) => (
        <div
          key={item.id || item.agendaId || index}
          style={{
            padding: 16,
            borderRadius: 14,
            background: "var(--v3-bg-subtle, rgba(255,255,255,0.03))",
            border: "1px solid var(--v3-border-subtle, rgba(255,255,255,0.07))",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 14,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 14, flex: "1 1 240px", minWidth: 0 }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                background: "rgba(238, 63, 124, 0.12)",
                border: "1px solid rgba(238, 63, 124, 0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#EE3F7C",
                flexShrink: 0,
              }}
            >
              <Clock size={20} />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "var(--v3-text-main, #fff)" }}>
                {item.title}
              </div>

              {item.description && (
                <div style={{ fontSize: 12, color: "var(--v3-text-muted, rgba(255,255,255,0.5))", overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                  {item.description}
                </div>
              )}

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 2 }}>
                {(item.startTime || item.endTime) && (
                  <span style={{ fontSize: 11, fontWeight: 600, color: "#EE3F7C", background: "rgba(238,63,124,0.1)", padding: "2px 8px", borderRadius: 6, display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <Clock size={11} /> {formatTime(item.startTime)} {item.endTime ? `- ${formatTime(item.endTime)}` : ''}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <button
              type="button"
              className="v3-btn-secondary"
              style={{ fontSize: 12, padding: "6px 12px" }}
              onClick={() => onEditAgenda(item)}
              title="Edit Agenda"
            >
              <Pencil size={13} /> Edit
            </button>
            <button
              type="button"
              className="v3-btn-secondary"
              style={{ fontSize: 12, padding: "6px 12px", color: "#FF5D73", borderColor: "rgba(255,93,115,0.2)" }}
              onClick={() => onDeleteAgenda(item.agendaId || item.id || '')}
              title="Delete Agenda"
            >
              <Trash2 size={13} /> Delete
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AgendaList;
